"use client";

import { useEffect, useState } from "react";
import { displayName, initials, timeAgo } from "@/lib/relative-time";

type Entry = {
  id: string;
  text: string;
  userEmail: string;
  userName?: string | null;
  createdAt: string;
  done: boolean;
};

export function FeedbackBoard({ currentUserEmail, isAdmin }: { currentUserEmail: string; isAdmin: boolean }) {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  // The board lives in a narrow column, so it starts folded and opens when someone wants it.
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetch("/api/feedback")
      .then((r) => (r.ok ? r.json() : { entries: [] }))
      .then((body) => setEntries(body.entries ?? []))
      .catch(() => {});
  }, [open]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const res = await fetch("/api/feedback", { method: "POST", body: JSON.stringify({ text }) });
      if (res.ok) {
        const created: Entry = await res.json();
        setEntries((prev) => [created, ...prev]);
        setText("");
      }
    } finally {
      setSending(false);
    }
  }

  async function toggleDone(entry: Entry) {
    const done = !entry.done;
    setEntries((prev) => prev.map((e) => (e.id === entry.id ? { ...e, done } : e)));
    await fetch(`/api/feedback/${entry.id}`, { method: "PATCH", body: JSON.stringify({ done }) }).catch(() => {});
  }

  async function remove(entry: Entry) {
    setEntries((prev) => prev.filter((e) => e.id !== entry.id));
    await fetch(`/api/feedback/${entry.id}`, { method: "DELETE" }).catch(() => {});
  }

  const openCount = entries.filter((e) => !e.done).length;

  return (
    <div className="border-t border-white/10 pt-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="kicker flex w-full items-center justify-between px-1 text-left hover:text-foreground"
      >
        <span>Жалобы и предложения</span>
        <span aria-hidden>{open ? "−" : "+"}</span>
      </button>

      {open && (
        <div className="mt-2 space-y-2">
          <form onSubmit={handleSubmit} className="space-y-1">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={500}
              rows={2}
              placeholder="Что мешает или чего не хватает?"
              className="w-full resize-none rounded border border-white/10 bg-white/5 p-1.5 text-xs text-foreground placeholder:text-muted-gray"
            />
            <button
              type="submit"
              disabled={!text.trim() || sending}
              className="w-full rounded bg-signal-red px-2 py-1 text-xs text-white hover:opacity-90 disabled:opacity-40"
            >
              Отправить
            </button>
          </form>

          <ul className="max-h-64 space-y-2 overflow-y-auto">
            {entries.length === 0 && <li className="px-1 text-xs text-muted-gray">Пока пусто.</li>}
            {entries.map((entry) => {
              // Anyone can mark their own note handled; an admin tidies up after everyone.
              const mine = entry.userEmail === currentUserEmail;
              return (
                <li key={entry.id} className={`rounded bg-white/5 p-1.5 text-xs ${entry.done ? "opacity-50" : ""}`}>
                  <div className="mb-0.5 flex items-center gap-1 text-[10px] text-muted-gray">
                    <span
                      aria-hidden
                      className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-signal-red text-[9px] text-white"
                    >
                      {initials(entry.userEmail, entry.userName)}
                    </span>
                    <span className="truncate">{displayName(entry.userEmail, entry.userName)}</span>
                    <span className="ml-auto shrink-0">{timeAgo(entry.createdAt)}</span>
                  </div>
                  <p className={`whitespace-pre-wrap break-words ${entry.done ? "line-through" : "text-foreground"}`}>
                    {entry.text}
                  </p>
                  {(mine || isAdmin) && (
                    <div className="mt-1 flex gap-2 text-[10px] text-muted-gray">
                      <button type="button" onClick={() => toggleDone(entry)} className="hover:text-foreground">
                        {entry.done ? "вернуть" : "решено"}
                      </button>
                      <button type="button" onClick={() => remove(entry)} className="hover:text-signal-red">
                        удалить
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {openCount > 0 && <p className="px-1 text-[10px] text-muted-gray">Нерешённых: {openCount}</p>}
        </div>
      )}
    </div>
  );
}
